import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

import {FormInput} from '../formInput';
import {Select} from '../select';
import {ScrollToAccept} from '../scrollToAccept';
import {Button} from '../button';
import {Tooltip} from '../tooltip';
import {Header} from '../header';
import {LoadingIcon} from '../loadingIcon';
import {LocksmithForm} from './LocksmithForm';
import {PasswordUtils, Validators} from 'services';
import {TranslationMixin} from 'mixins/translationMixin';
import {WithCommonTranslations, WithFormValidation, WithUserTranslations,} from '../hocs/';


import './ProfileForm.css';

export const ProfileForm = WithCommonTranslations(WithUserTranslations(
    WithFormValidation(class extends TranslationMixin(React.Component) {
        constructor(props) {
            super(props);
            this.state = {
                disabled: true,
                customPasswordError: '',
        };

        this.pwRef = React.createRef();
        this.pw2Ref = React.createRef();
        this.passwordRegex = PasswordUtils.getRegexPattern();
        this.handleInputChange = this.handleInputChange.bind(this);
        this.validatePasswords = this.validatePasswords.bind(this);
        this.onlyNumbersOnKeyPress = Validators.onlyNumbersOnKeyPress.bind(
            this);
      }

      componentDidUpdate(prevProps) {
        if (this.state.customPasswordError) {
          const {translations} = this.props;
          const key = "ERROR_PASSWORDS_DONT_MATCH";

          if (!prevProps.translations ||
              (prevProps.translations[key] !== translations[key])) {
            this.validatePasswords();
          }
        }
      }

      handleCheckValue = (e) => {
        this.setState({disabled: !e});
      };

      handleInputChange(event, callback) {
        const target = event.target;
        const value = target.type === 'checkbox' ?
            target.checked :
            target.value;
        const name = target.name;
        const self = this;

        this.props.onChange(name, value, function() {
          if (self.props.showLogin) {
            self.validatePasswords();
          }

          if (typeof callback === 'function') {
            callback();
          }
        });
      }

      static getDerivedStateFromProps(props, state) {
        if (!props.showTerms) {
          return {disabled: false};
        } else {
          return {};
        }
      }

      handleSelectChange = (name) => (value) => {
        this.props.onChange(name, value);
      };

      validatePasswords() {
        const error = this.getTranslation('ERROR_PASSWORDS_DONT_MATCH');
        const pw = this.getInput(this.pwRef);
        const pw2 = this.getInput(this.pw2Ref);
        let customError = '';
        if (!pw.validity.patternMismatch) {
          pw.setCustomValidity('');
          pw2.setCustomValidity('');
          if (!Validators.validateEqual(pw, pw2, error)) {
            customError = error;
          }
          this.setState({customPasswordError: customError});
        } else {
          pw2.setCustomValidity('');
          this.setState({customPasswordError: ''});
        }
      }

      getInput(ref) {
        const dom = ReactDOM.findDOMNode(ref.current);
        return dom.querySelector('input');
      }

      render() {
          const terms = this.props.termsHtmlString;
          return (
              <div className="profile-form container">
                  <Header level={2}>{this.getTranslation(
                      'LABEL_CUSTOMER_INFORMATION')}</Header>
                  <div className="row justify-content-between">
                      <div className="col-md-6">
                          <FormInput label={this.getTranslation('LABEL_FIRST_NAME')}
                                     name="firstName" required={true}
                                     id="firstName"
                             maxLength={100}
                             readOnly={this.props.readOnly}
                             autoComplete="given-name"
                             displayError={this.props.isValidated}
                             value={this.props.formValues.firstName}
                             onChange={this.handleInputChange}/>
                  <FormInput label={this.getTranslation('LABEL_MIDDLE_NAME')}
                             maxLength={100}
                             name="middleName"
                             id="middleName"
                             autoComplete="additional-name"
                             readOnly={this.props.readOnly}
                             displayError={this.props.isValidated}
                             value={this.props.formValues.middleName}
                             onChange={this.handleInputChange}/>
                  <FormInput label={this.getTranslation('LABEL_LAST_NAME')}
                             name="lastName"
                             id="lastName"
                             required={true}
                             maxLength={100}
                             autoComplete="family-name"
                             readOnly={this.props.readOnly}
                             displayError={this.props.isValidated}
                             value={this.props.formValues.lastName}
                             onChange={this.handleInputChange}/>
                  <FormInput label={this.getTranslation('LABEL_EMAIL')}
                             id="email"
                             name="email" required={true}
                             type="email"
                             maxLength={100}
                             autoComplete="email"
                             readOnly={this.props.readOnly}
                             displayError={this.props.isValidated}
                             value={this.props.formValues.email}
                             onChange={this.handleInputChange}/>
                  <FormInput label={this.getTranslation('LABEL_ADDRESS_1')}
                             name="addressLine1"
                             id="addressLine1"
                             required={true}
                             maxLength={100}
                             autoComplete="address-line1"
                             readOnly={this.props.readOnly}
                             displayError={this.props.isValidated}
                             value={this.props.formValues.addressLine1}
                             onChange={this.handleInputChange}/>
                  <FormInput label={this.getTranslation('LABEL_ADDRESS_2')}
                             name="addressLine2"
                             id="addressLine2"
                             maxLength={100}
                             autoComplete="address-line2"
                             readOnly={this.props.readOnly}
                             displayError={this.props.isValidated}
                             value={this.props.formValues.addressLine2}
                             onChange={this.handleInputChange}/>
                  <FormInput label={this.getTranslation('LABEL_CITY')}
                             name="city"
                             id="city"
                             required={true}
                             maxLength={100}
                             autoComplete="address-level2"
                             readOnly={this.props.readOnly}
                             displayError={this.props.isValidated}
                             value={this.props.formValues.city}
                             onChange={this.handleInputChange}/>

                  <Select label={this.getTranslation('LABEL_STATE')}
                          name="state"
                          id="state"
                          autoComplete="address-level1"
                          required={this.props.requireState}
                          options={this.props.states}
                          value={this.props.formValues.state}
                          readOnly={this.props.readOnly}
                          error={this.props.isValidated &&
                          this.props.requireState &&
                          !this.props.formValues.state}
                          errorMessage={this.getTranslation('ERROR_REQUIRED')}
                          onChange={this.handleSelectChange('state')}/>

                  <Select label={this.getTranslation('LABEL_COUNTRY')}
                          name="country"
                          id="country"
                          autoComplete="country"
                          required={true}
                          options={this.props.countries}
                          value={this.props.formValues.country}
                          readOnly={this.props.readOnly}
                          error={this.props.isValidated &&
                          !this.props.formValues.country}
                          errorMessage={this.getTranslation('ERROR_REQUIRED')}
                          onChange={this.handleSelectChange('country')}/>

                  <FormInput label={this.getTranslation('LABEL_ZIP')}
                             name="zipCode"
                             id="zipCode"
                             required={true}
                             maxLength={12}
                             autoComplete="postal-code"
                             readOnly={this.props.readOnly}
                             displayError={this.props.isValidated}
                             value={this.props.formValues.zipCode}
                             onChange={this.handleInputChange}/>

                  <FormInput label={this.getTranslation('LABEL_PHONE')}
                             name="phone"
                             id="phone"
                             maxLength={15}
                             onKeyPress={this.onlyNumbersOnKeyPress}
                             autoComplete="tel" pattern="\d+"
                             readOnly={this.props.readOnly}
                             displayError={this.props.isValidated}
                             value={this.props.formValues.phone}
                             onChange={this.handleInputChange}/>

                      </div>
                      <div className="col-md-6 col-lg-5">
                          {this.props.showLocksmithFields &&
                          <LocksmithForm
                              className="mb-5"
                              onChange={this.handleInputChange}
                              regionCode={this.props.regionCode}
                              readOnly={this.props.readOnly || this.props.locksmithIDReadOnly}
                              locksmithID={this.props.formValues.locksmithID}
                              locksmithPasscode={this.props.formValues.locksmithPasscode}
                              hidePasscode={this.props.hidePasscode}
                              showDelete={this.props.showDeleteLocksmithID}
                              onDelete={this.props.onDeleteLocksmithID}
                              isValidated={this.props.isValidated}/>
                  }
                    <Header level={2}>{this.getTranslation(
                        'TITLE_ACDELCO_ACCOUNT')}&nbsp;
                        <Tooltip text={this.getTranslation(
                            'MESSAGE_ACDELCO_ACCOUNT_TOOLTIP')}/></Header>
                    <FormInput label={this.getTranslation('LABEL_ACCOUNT_NUMBER')}
                               name="acdelcoAcctNum"
                               id="acdelcoAcctNum"
                               maxLength={10}
                               pattern="\d+"
                               data-pattern-message={this.getTranslation(
                                 'ERROR_ACCOUNT_NUMBER_PATTERN')}
                             readOnly={this.props.readOnly}
                             displayError={this.props.isValidated}
                             value={this.props.formValues.acdelcoAcctNum}
                             onChange={this.handleInputChange}/>

                  <Header level={2}>{this.getTranslation(
                      'LABEL_COMPANY_INFORMATION')}</Header>

                    {this.props.showVATField &&
                    <FormInput label={this.getTranslation('LABEL_COMPANY_NAME')}
                               name="companyName"
                               id="companyName"
                               required={this.props.requireVAT || this.props.formValues.vat != ""}
                               maxLength={100}
                               readOnly={this.props.readOnly}
                               displayError={this.props.isValidated}
                               value={this.props.formValues.companyName}
                               onChange={this.handleInputChange}/>
                    }
                    {this.props.showVATField &&
                    <FormInput label={this.getTranslation(this.props.taxIdLabelId || 'LABEL_VAT')}
                               name="vat"
                               id="vat"
                               required={this.props.requireVAT}
                               maxLength={50}
                               readOnly={this.props.readOnly}
                               displayError={this.props.isValidated}
                               value={this.props.formValues.vat}
                               onChange={this.handleInputChange}/>
                    }

                    <FormInput label={this.getTranslation('LABEL_COMPANY_EMAIL')}
                             id="companyEmail"
                             name="companyEmail"
                             type="email"
                             maxLength={100}
                             autoComplete="email"
                             readOnly={this.props.readOnly}
                             displayError={this.props.isValidated}
                             value={this.props.formValues.companyEmail}
                             onChange={this.handleInputChange}/>



                  <Header level={2}>{this.getTranslation(
                      'LABEL_PREFERRED_LANGUAGE')}</Header>

                  <Select
                      label={this.getTranslation('LABEL_PREFERRED_LANGUAGE')}
                      name="preferredLanguage"
                      id="preferredLanguage"
                      required={true}
                      options={this.props.languages}
                      value={this.props.formValues.preferredLanguage}
                      readOnly={this.props.readOnly}
                      onChange={(v) => this.props.onChange('preferredLanguage',
                          v)}/>

                  {this.props.renderExtraFields ?
                      this.props.renderExtraFields(this.props) :
                      ''}
                </div>
              </div>
              {this.props.showLogin &&
              <div className="row">
                <div className="col-md-6">
                  <Header level={2}>{this.getTranslation(
                      'LABEL_LOGIN_AND_PASSWORD')}</Header>
                  <FormInput label={this.getTranslation('LABEL_LOGIN_NAME')}
                             name="username"
                             id="username"
                             minLength={8}
                             pattern="^((?!\s).){8,20}$"
                             data-pattern-message={this.getTranslation(
                                 'ERROR_USERNAME_PATTERN')}
                             message={this.getTranslation(
                                 'MESSAGE_USERNAME_TIP')}
                             required={true}
                             readOnly={this.props.readOnly}
                             displayError={this.props.isValidated}
                             value={this.props.formValues.username}
                             onChange={this.handleInputChange}/>
                  <FormInput label={this.getTranslation('LABEL_PASSWORD')}
                             name="password"
                             id="password"
                             type="password"
                             autoComplete="off"
                             ref={this.pwRef}
                             required={true}
                             pattern={this.passwordRegex}
                             message={this.getTranslation(
                                 'MESSAGE_PASSWORD_TIP')}
                             readOnly={this.props.readOnly}
                             displayError={this.props.isValidated}
                             value={this.props.formValues.password}
                             customError={this.state.customPasswordError}
                             onChange={this.handleInputChange}/>
                  <FormInput
                      label={this.getTranslation('LABEL_CONFIRM_PASSWORD')}
                      name="confirmPassword"
                      id="confirmPassword"
                      type="password"
                      autoComplete="off"
                      ref={this.pw2Ref}
                      required={true}
                      readOnly={this.props.readOnly}
                      displayError={this.props.isValidated}
                      maxLength={20}
                      value={this.props.formValues.confirmPassword}
                      customError={this.state.customPasswordError}
                      onChange={this.handleInputChange}/>
                </div>
              </div>}

              <div className="row">
                <div className="col-sm-12 col-lg-9 col-xl-6">
                  {this.props.showTerms &&
                  <div className="terms mb-5">
                    <Header level={2} className="mb-3">{this.getTranslation(
                        'LABEL_TERMS_AND_CONDITIONS')}</Header>
                    <div><span
                        className="font-weight-bold">{this.getTranslation(
                        'MESSAGE_TDS_TERMS')}</span> <span
                        className="font-italic">{this.getTranslation(
                        'MESSAGE_PLEASE_SCROLL_TERMS')}</span></div>
                    {terms &&
                    <ScrollToAccept
                        disabled={this.props.readOnly}
                        checkboxLabel={this.getTranslation(
                            'LABEL_REGISTRATION_AGREE_TERMS')}
                        onChange={this.handleCheckValue}>
                      <div dangerouslySetInnerHTML={{
                        __html: terms,
                      }}></div>
                    </ScrollToAccept>}
                  </div>
                  }
                  {this.props.renderCaptcha ?
                      this.props.renderCaptcha(this.props) : ''}

                  {!this.props.readOnly &&
                  <React.Fragment>
                    <Button type="submit"
                            variant="primary"
                            disabled={this.props.loading ||
                            this.state.disabled ||
                            this.props.readOnly}>
                      <LoadingIcon
                          display={this.props.loading}/>{this.getTranslation(
                        'BUTTON_SAVE')}</Button>
                    <Button type="button"
                            variant="link"
                            onClick={this.props.onCancel}>{this.getTranslation(
                        'BUTTON_CANCEL')}</Button>
                  </React.Fragment>
                  }
                </div>
              </div>
            </div>
        );
      }
    })));

ProfileForm.propTypes = {
    showTerms: PropTypes.bool.isRequired,
    showLogin: PropTypes.bool.isRequired,
    termsHtmlString: PropTypes.node,
    renderExtraFields: PropTypes.func,
    showDeleteLocksmithID: PropTypes.bool,
    onDeleteLocksmithID: PropTypes.func,
    renderCatpcha: PropTypes.func,
    showVATField: PropTypes.bool,
    showLocksmithFields: PropTypes.bool,
    locksmithIDReadOnly: PropTypes.bool,
    languages: PropTypes.array,
    requireState: PropTypes.bool,
    requireVAT: PropTypes.bool,
    taxIdLabelId: PropTypes.string,
};